import { Injectable, Type } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, expand, map, reduce, shareReplay, switchMap, take } from 'rxjs/operators';
import * as feather from 'feather-icons';
import { ToastrService } from 'ngx-toastr';
import {
  ErpAffichagesApi,
  ErpCroudTvaComponent,
  ErpCroudTaxeComponent,
  ErpCroudMarqueCategoryComponent,
  ErpTaxe,
  ErpTaxeApiResponse,
  ErpTva,
  ErpPageHeaderConfig,
  ErpPaginatedProducts,
  ErpClient,
  ErpPaginatedClients,
  ErpFournisseur,
  ErpPaginatedFournisseurs
} from '@erp/affichages';
import { ProductHistoriquePayload } from './product-historique.types';
import { ProductXlsxImportService } from './product-xlsx-import.service';
import { createEmptyProductImportWorkbook } from 'app/main/pages/shared/document/document-product-xlsx-import.helper';
import { GammeListeBulkRequest, ProductsService } from 'app/services/products/products.service';
import { CategorieService } from 'app/services/categorie-unite-marque/categorie.service';
import { MarqueService } from 'app/services/categorie-unite-marque/marque.service';
import { UniteService } from 'app/services/categorie-unite-marque/unite.service';
import { TaxeService } from 'app/main/parametre/taxe-tva/parametre-taxe/taxe.service';
import { TvaService } from 'app/main/parametre/taxe-tva/parametre-tva/tva.service';
import { StockService } from 'app/services/stock/stock.service';
import { getEnterpriseStationIdFromLocalStorage } from 'app/services/enterprise-context';
import { StationService } from 'app/services/station.service';
import { WarehouseService } from 'app/services/warehouse/warehouse.service';
import { PrixService } from 'app/main/parametre/liste-prix/prix.service';
import { ParametreService } from 'app/main/parametre/main-parametre/parametre.service';
import { HeaderService, HeaderConfig } from 'app/services/header/header.service';
import { ParametreDonneesStateService } from 'app/main/parametre/parametre-donnees/parametre-donnees-state.service';
import { ParametreDonneesApiService } from 'app/main/parametre/parametre-donnees/parametre-donnees-api.service';
import {
  DataExportFormatDto,
  resolveRefModuleTableDbId
} from 'app/main/parametre/parametre-donnees/parametre-donnees-api.models';
import { ParamTablesService } from 'app/services/parametrage/param-tables.service';
import { PaginatedResponse } from 'app/models/paginatedResponse';
import { Product } from 'app/models/Product';
import { Client } from 'app/models/client';
import { ClientService } from 'app/services/Client/client.service';
import { ClienEventService } from 'app/services/Client/clien-event.service';
import {
  CaisseClient,
  CreateCaisseClientPayload,
  LogicielCaisseClientService
} from 'app/services/logiciel-caisse/logiciel-caisse-client.service';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { readObservableError } from 'app/services/logiciel-caisse/logiciel-caisse-api.helper';
import { FournisseurService } from 'app/services/Fournisseur/fournisseur.service';
import { FournisseurEventService } from 'app/services/Fournisseur/fournisseur-event.service';
import { Fournisseur } from 'app/models/fournisseur';
import { DocumentService } from 'app/services/document/document.service';
import { TresorerieService } from 'app/services/tresorerie/tresorerie.service';
import { PaiementService } from 'app/services/paiement/paiement.service';
import { SharedComponent } from 'app/main/pages/shared/shared.component';
import { environment } from 'environments/environment';

type XlsxModule = typeof import('xlsx');
type JsPdfBundle = {
  jsPDF: new (...args: unknown[]) => unknown;
  autoTable: (doc: unknown, options: unknown) => void;
};

@Injectable()
export class ErpAffichagesApiService implements ErpAffichagesApi {
  private tvaAdded$ = new Subject<void>();
  private tvaUpdated$ = new Subject<ErpTva>();
  private taxeAdded$ = new Subject<void>();
  private taxeUpdated$ = new Subject<ErpTaxe>();
  private tvaModalRef: NgbModalRef | null = null;
  private taxeModalRef: NgbModalRef | null = null;
  private xlsxModulePromise: Promise<XlsxModule> | null = null;
  private jsPdfBundlePromise: Promise<JsPdfBundle> | null = null;
  private readonly mergedClientsCache = new Map<
    number,
    { expiresAt: number; stream: Observable<ErpClient[]> }
  >();
  private readonly mergedClientsCacheTtlMs = 15_000;

  private invalidateMergedClientsCache(stationId?: unknown): void {
    const id = Number(stationId);
    if (Number.isFinite(id) && id > 0) {
      this.mergedClientsCache.delete(id);
      return;
    }
    this.mergedClientsCache.clear();
  }

  private loadXlsxModule(): Promise<XlsxModule> {
    if (!this.xlsxModulePromise) {
      this.xlsxModulePromise = import('xlsx');
    }
    return this.xlsxModulePromise;
  }

  private loadJsPdfBundle(): Promise<JsPdfBundle> {
    if (!this.jsPdfBundlePromise) {
      this.jsPdfBundlePromise = Promise.all([import('jspdf'), import('jspdf-autotable')]).then(
        ([jspdfModule, autoTableModule]) => ({
          jsPDF: jspdfModule.default,
          autoTable: autoTableModule.default,
        })
      );
    }
    return this.jsPdfBundlePromise;
  }

  constructor(
    private productsService: ProductsService,
    private productXlsxImportService: ProductXlsxImportService,
    private clientService: ClientService,
    private clientEventService: ClienEventService,
    private lcClientService: LogicielCaisseClientService,
    private lcAuth: LogicielCaisseAuthService,
    private fournisseurService: FournisseurService,
    private fournisseurEventService: FournisseurEventService,
    private categorieService: CategorieService,
    private marqueService: MarqueService,
    private uniteService: UniteService,
    private taxeService: TaxeService,
    private tvaService: TvaService,
    private stockService: StockService,
    private stationService: StationService,
    private warehouseService: WarehouseService,
    private prixService: PrixService,
    private parametreService: ParametreService,
    private headerService: HeaderService,
    private paramDonneesState: ParametreDonneesStateService,
    private paramDonneesApi: ParametreDonneesApiService,
    private paramTablesService: ParamTablesService,
    private documentService: DocumentService,
    private tresorerieService: TresorerieService,
    private paiementService: PaiementService,
    private modalService: NgbModal,
    private router: Router,
    private location: Location,
    private toastr: ToastrService
  ) {}

  getCategories(stationId: number, clientId: number): Observable<{ id: number; libelle: string }[]> {
    return this.categorieService.getAll(stationId, clientId) as Observable<{ id: number; libelle: string }[]>;
  }

  getMarques(stationId: number, clientId: number): Observable<{ id: number; libelle: string }[]> {
    return this.marqueService.getAll(stationId, clientId) as Observable<{ id: number; libelle: string }[]>;
  }

  getUnites(stationId: number, clientId: number): Observable<{ id: number; libelle: string }[]> {
    return this.uniteService.getAll(stationId, clientId) as Observable<{ id: number; libelle: string }[]>;
  }

  addCategorie(categorie: {
    id?: number;
    user_id: number;
    station_id: number;
    libelle: string;
  }): Observable<{ id: number; libelle: string }> {
    return this.categorieService.addCategorie(categorie as never).pipe(
      map((result) => ({ id: result.id, libelle: result.libelle }))
    );
  }

  updateCategorie(
    categorie: { id: number; libelle: string },
    stationId: number
  ): Observable<{ id: number; libelle: string }> {
    return this.categorieService.updateCategorie(categorie as never, stationId).pipe(
      map((result) => ({ id: result.id, libelle: result.libelle }))
    );
  }

  addMarque(marque: {
    id?: number;
    user_id: number;
    station_id: number;
    libelle: string;
  }): Observable<{ id: number; libelle: string }> {
    return this.marqueService.addMarque(marque as never).pipe(
      map((result) => ({ id: result.id, libelle: result.libelle }))
    );
  }

  updateMarque(
    marque: { id: number; libelle: string },
    stationId: number
  ): Observable<{ id: number; libelle: string }> {
    return this.marqueService.updateMarque(marque as never, stationId).pipe(
      map((result) => ({ id: result.id, libelle: result.libelle }))
    );
  }

  addUnite(unite: {
    id?: number;
    user_id: number;
    station_id: number;
    libelle: string;
  }): Observable<{ id: number; libelle: string }> {
    return this.uniteService.addUnite(unite as never).pipe(
      map((result) => ({ id: result.id, libelle: result.libelle }))
    );
  }

  updateUnite(
    unite: { id: number; libelle: string },
    stationId: number
  ): Observable<{ id: number; libelle: string }> {
    return this.uniteService.updateUnite(unite as never, stationId).pipe(
      map((result) => ({ id: result.id, libelle: result.libelle }))
    );
  }

  getTaxes(stationId: number) {
    return this.taxeService.getTaxeList(stationId) as Observable<{
      taxes?: { id: number; nom: string; statut?: boolean }[];
    }>;
  }

  getTaxeListResponse(stationId: number): Observable<ErpTaxeApiResponse> {
    return this.taxeService.getTaxeList(stationId) as Observable<ErpTaxeApiResponse>;
  }

  createTaxe(taxe: Record<string, unknown>, stationId: number): Observable<ErpTaxeApiResponse> {
    return this.taxeService.createTaxe(taxe as never, stationId) as Observable<ErpTaxeApiResponse>;
  }

  updateTaxe(taxe: Record<string, unknown>, stationId: number): Observable<ErpTaxeApiResponse> {
    return this.taxeService.updateTaxe(taxe as never, stationId) as Observable<ErpTaxeApiResponse>;
  }

  getTvaList(stationId: number) {
    return this.tvaService.getTvaList(stationId) as Observable<{
      tvaListe?: { id: number; valeur: number; statut?: boolean }[];
    }>;
  }

  createTva(tva: Record<string, unknown>, stationId: number) {
    return this.tvaService.createTva(tva as never, stationId);
  }

  updateTva(tva: Record<string, unknown>, stationId: number) {
    return this.tvaService.updateTva(tva as never, stationId);
  }

  notifyTvaMessage(message: unknown): void {
    this.tvaService.sendMessage(message);
  }

  getPrixList(stationId: number) {
    return this.prixService.getPrix(stationId) as Observable<{ id?: number; libelle: string; statut?: boolean; station_id?: number; client_id?: number }[]>;
  }

  addPrix(prix: Record<string, unknown>, stationId: number) {
    return this.prixService.addPrix(prix as never, stationId).pipe(
      map((result) => ({ id: result.id, libelle: result.libelle, statut: result.statut }))
    );
  }

  updatePrix(prix: Record<string, unknown>, stationId: number) {
    return this.prixService.updatePrix(prix as never, stationId);
  }

  updatePrixEtat(id: number, statut: boolean, stationId: number) {
    return this.prixService.updatePrixEtat(id, statut, stationId);
  }

  getFileBaseUrl(): string {
    return environment.file_url;
  }

  getProductsByStation(
    stationId: number,
    page: number,
    limit: number,
    isArchive: boolean
  ): Observable<ErpPaginatedProducts> {
    return this.productsService
      .getProductsByStationId(stationId, page, limit, isArchive)
      .pipe(map((r: PaginatedResponse<Product>) => r as ErpPaginatedProducts));
  }

  getProductDetails(productId: number): Observable<Record<string, unknown>> {
    return this.productsService.getDetailsProduct(productId).pipe(
      map((r) => r as unknown as Record<string, unknown>)
    );
  }

  addProduct(stationId: number, product: Record<string, unknown>): Observable<unknown> {
    return this.productsService.addProduct(stationId, product);
  }

  updateProduct(
    productId: number,
    product: Record<string, unknown>,
    stationClient?: { station_id: number; client_id: number }
  ): Observable<unknown> {
    return this.productsService.updateProduct(productId, product, stationClient);
  }

  postProductHistorique(payload: ProductHistoriquePayload): Observable<unknown> {
    return this.productsService.postProductHistorique(payload);
  }

  archiverProduct(id: number) {
    return this.productsService.archiverProduct(id);
  }

  getTotalStockByErpProductId(productId: number) {
    return this.stockService.getTotalStockByErpProductId(productId);
  }

  getTotalStockByErpProductIds(
    productIds: number[],
    _productsMeta?: Array<{ id: number; code_barre?: string | null }>
  ) {
    return this.stockService.getTotalStockByErpProductIds(productIds);
  }

  getStockDetailsByProduct(productErpId: number): Observable<unknown> {
    return this.stockService.getStockDetailsByProduct(productErpId);
  }

  getStockContextForMouvements(erpProductId: number): Observable<{ stockId: number | null; meta: unknown }> {
    return this.stockService.getStockContextForMouvements(erpProductId);
  }

  getMouvementsStockByStockProductId(stockId: number): Observable<unknown> {
    return this.stockService.getMouvementsStockByStockProductId(stockId);
  }

  getDocumentById(documentId: number): Observable<unknown> {
    return this.documentService.getDocumentById(documentId);
  }

  getWarehouseDetail(entrepotId: number, clientId: number, stationId: number): Observable<unknown> {
    return this.warehouseService.detailEntrepot(entrepotId, clientId, stationId);
  }

  getStationDetails(stationId: number): Observable<unknown> {
    return this.stationService.getStation(stationId);
  }

  getStationContext(): { userId: number; stationId: number } {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
      return {
        userId: user?.id || user?.client_id || 0,
        stationId: getEnterpriseStationIdFromLocalStorage()
      };
    } catch {
      return { userId: 0, stationId: 0 };
    }
  }

  getPrixDetailById(stationId: number, prixId: number): Observable<{ libelle?: string }> {
    return this.prixService.getPrixById(stationId, prixId) as Observable<{ libelle?: string }>;
  }

  getLastPrixHt(productId: number): Observable<unknown> {
    return this.documentService.getLastPrixHt(productId);
  }

  navigateToDocumentValidation(routeBase: string, documentId: number): void {
    this.router.navigate([routeBase, 'action', 'validation'], { queryParams: { id: documentId } });
  }

  getWarehouses(clientId: number, stationId: number): Observable<unknown[]> {
    return this.warehouseService.getAllWarehouses(clientId, stationId) as Observable<unknown[]>;
  }

  uploadProductFile(formData: FormData): Observable<{ path?: string }> {
    return this.stationService.uploadFile(formData) as Observable<{ path?: string }>;
  }

  getImagesBaseUrl(): string {
    return environment.url_images;
  }

  navigateToAddProduct(): void {
    this.router.navigate(['/bibliotheque-test/ajout-produit']);
  }

  navigateToProductsList(): void {
    this.router.navigate(['/bibliotheque-test/actif']);
  }

  navigateToProductDetailVue(productId: number): void {
    this.router.navigate(['/bibliotheque-test/detail-produit', productId, 'vue-ensemble']);
  }

  navigateToDetail(productId: number): void {
    this.router.navigate(['/bibliotheque-test/detail-produit', productId]);
  }

  navigateToEdit(productId: string | number): void {
    this.router.navigate(['/bibliotheque-test/modifier-produit', productId], {
      queryParams: { step: 1 }
    });
  }

  navigateToDuplicateProduct(
    productId: number,
    opts?: { duplicateFamily?: boolean; familyProductIds?: number[] }
  ): void {
    const id = Math.floor(Number(productId));
    if (!Number.isFinite(id) || id <= 0) {
      this.toastError('ID produit manquant pour la duplication.', 'Erreur');
      return;
    }
    this.router.navigate(['/bibliotheque-test/ajout-produit'], {
      state: {
        isDuplicateMode: true,
        sourceProductId: id,
        isDuplicateFamily: !!opts?.duplicateFamily,
        familyProductIds: Array.isArray(opts?.familyProductIds)
          ? opts!.familyProductIds!.map((x) => Math.floor(Number(x))).filter((x) => x > 0)
          : []
      }
    });
  }

  getProductTabCounts(stationId: number): Observable<{ active: number; archived: number }> {
    const limitForCount = 1;
    const active$ = this.productsService
      .getProductsByStationId(stationId, 1, limitForCount, false)
      .pipe(map((r) => r.meta?.total ?? 0));
    const archived$ = this.productsService
      .getProductsByStationId(stationId, 1, limitForCount, true)
      .pipe(map((r) => r.meta?.total ?? 0));
    return forkJoin({ active: active$, archived: archived$ });
  }

  loadProductsExportDropdownItems(): Observable<{ label: string; action: () => void; icon?: string }[]> {
    const ctx = this.getStationContext();
    if (!ctx.stationId || !ctx.userId) {
      return of([
        {
          label: 'Session invalide',
          action: () => this.toastError('Session invalide : station ou utilisateur introuvable.', 'Export')
        }
      ]);
    }

    return this.paramDonneesState.ensureRefModules().pipe(
      map(() => this.getProduitsRefModuleSilent()),
      switchMap((ref) => {
        if (!ref) {
          return of([] as DataExportFormatDto[]);
        }
        return this.paramDonneesApi
          .getDataExportFormatsByTable(ref.tableDbId, ctx.stationId, ctx.userId)
          .pipe(catchError(() => of([] as DataExportFormatDto[])));
      }),
      map((formats) => {
        const orderedFormats = Array.isArray(formats) ? formats : [];
        if (!orderedFormats.length) {
          return [
            {
              label: 'Aucun modèle d’export actif',
              action: () =>
                this.toastWarning(
                  'Aucun modèle d’export actif n’est configuré. Activez-en un dans Paramétrage → Export.',
                  'Export'
                )
            }
          ];
        }
        return orderedFormats.map((fmt, idx) => {
          const exportFormatId = Number((fmt as DataExportFormatDto)?.id);
          const type = String((fmt as DataExportFormatDto)?.type ?? '')
            .toLowerCase()
            .trim();
          const label = `Modèle ${idx + 1}${type ? `(${type})` : ''}`;
          const actif = (fmt as DataExportFormatDto)?.actif === true;
          return {
            label,
            icon: this.iconForProductExportType(type),
            action: () => {
              if (!actif) {
                this.toastWarning('Ce modèle est inactif.', 'Export');
                return;
              }
              this.exportProductsListUsingModel(exportFormatId, type);
            }
          };
        });
      }),
      catchError(() =>
        of([
          {
            label: 'Erreur chargement modèles',
            action: () => this.toastError('Erreur lors du chargement des modèles d’export.', 'Export')
          }
        ])
      ),
      take(1)
    );
  }

  downloadProductsImportTemplate(): void {
    try {
      const workbook = createEmptyProductImportWorkbook();
      void this.loadXlsxModule()
        .then((XLSX) => {
          XLSX.writeFile(workbook, 'produit_template.xlsx');
          this.toastSuccess('Modèle téléchargé', 'Succès');
        })
        .catch(() => this.toastError('Erreur lors du téléchargement du modèle', 'Erreur'));
    } catch {
      this.toastError('Erreur lors du téléchargement du modèle', 'Erreur');
    }
  }

  importProductsFromXlsxFile(
    file: File
  ): Observable<{ importedCount: number; updatedCount: number; errors: string[] }> {
    const { userId, stationId } = this.getStationContext();
    return this.productXlsxImportService.importFromFile(file, 'reimport', stationId, userId);
  }

  getClientsByStation(
    stationId: number,
    page: number,
    limit: number,
    isArchive: boolean,
    clientType?: string,
    searchTerm?: string
  ): Observable<ErpPaginatedClients> {
    if (isArchive) {
      if (searchTerm?.trim()) {
        return this.fetchAllArchivedClients(stationId).pipe(
          map((clients) =>
            this.paginateClients(
              this.filterClientsBySearch(clients as ErpClient[], searchTerm),
              page,
              limit
            )
          )
        );
      }
      return this.clientService
        .getArchivedClient(stationId, page, limit)
        .pipe(map((r: PaginatedResponse<Client>) => r as ErpPaginatedClients));
    }

    return this.fetchMergedActiveClients(stationId).pipe(
      map((clients) => {
        const byType = this.filterClientsByType(clients, clientType);
        const filtered = this.filterClientsBySearch(byType, searchTerm);
        return this.paginateClients(filtered, page, limit);
      })
    );
  }

  getClientTabCounts(stationId: number): Observable<{
    individuel: number;
    professionnel: number;
    negoce: number;
    archived: number;
  }> {
    const archived$ = this.clientService
      .getArchivedClient(stationId, 1, 1)
      .pipe(map((r) => r.meta?.total ?? 0));

    return forkJoin({
      clients: this.fetchMergedActiveClients(stationId),
      archived: archived$
    }).pipe(
      map(({ clients, archived }) => {
        return {
          individuel: clients.filter((c) => this.normalizeClientType(c.type) === 'Individuel').length,
          professionnel: clients.filter((c) => this.normalizeClientType(c.type) === 'Professionnel').length,
          negoce: clients.filter((c) => this.normalizeClientType(c.type) === 'Négoce').length,
          archived
        };
      })
    );
  }

  private fetchAllActiveClients(stationId: number): Observable<Client[]> {
    const pageSize = 100;

    return this.clientService.getActiveClient(stationId, 1, pageSize).pipe(
      expand((response) => {
        const currentPage = response.meta?.page ?? 1;
        const totalPages = response.meta?.nb_total_pages ?? response.meta?.pages ?? 1;

        if (currentPage >= totalPages) {
          return EMPTY;
        }

        return this.clientService.getActiveClient(stationId, currentPage + 1, pageSize);
      }),
      map((response) => response.data ?? []),
      reduce((all, pageData) => all.concat(pageData), [] as Client[])
    );
  }

  getAllClientsByStation(
    stationId: number,
    isArchive: boolean,
    clientType?: string
  ): Observable<ErpClient[]> {
    if (isArchive) {
      return this.fetchAllArchivedClients(stationId).pipe(
        map((clients) => {
          if (!clientType) {
            return clients as ErpClient[];
          }
          const normalizedType = this.normalizeClientType(clientType);
          return clients.filter(
            (client) => this.normalizeClientType(client.type) === normalizedType
          ) as ErpClient[];
        })
      );
    }

    return this.fetchMergedActiveClients(stationId).pipe(
      map((clients) => this.filterClientsByType(clients, clientType))
    );
  }

  private filterClientsByType(clients: ErpClient[], clientType?: string): ErpClient[] {
    if (!clientType) {
      return clients;
    }
    const normalizedType = this.normalizeClientType(clientType);
    return clients.filter(
      (client) => this.normalizeClientType(client.type) === normalizedType
    );
  }

  /** Recherche globale avant pagination : référence ou nom uniquement. */
  private filterClientsBySearch(clients: ErpClient[], searchTerm?: string): ErpClient[] {
    const term = this.normalizeClientIdentityPart(searchTerm);
    if (!term) {
      return clients;
    }
    return clients.filter((client) => {
      const reference = this.normalizeClientIdentityPart(
        [client.identifiant_unique, client.code_client, client.id]
          .filter((value) => value != null && String(value).trim() !== '')
          .join(' ')
      );
      const name = this.normalizeClientIdentityPart(
        [client.nom_prenom, client.nom_entreprise].filter(Boolean).join(' ')
      );
      return reference.includes(term) || name.includes(term);
    });
  }

  private paginateClients(
    clients: ErpClient[],
    page: number,
    limit: number
  ): ErpPaginatedClients {
    const safeLimit = Math.max(1, Number(limit) || 10);
    const total = clients.length;
    const pages = Math.max(1, Math.ceil(total / safeLimit));
    const safePage = Math.min(Math.max(1, Number(page) || 1), pages);
    const start = (safePage - 1) * safeLimit;
    return {
      data: clients.slice(start, start + safeLimit),
      meta: {
        total,
        pages,
        nb_total_pages: pages,
        page: safePage,
        limit: safeLimit
      }
    };
  }

  /** Charge toutes les pages caisse ; une indisponibilité caisse ne bloque jamais l'ERP. */
  private fetchAllCaisseClientsSafe(): Observable<CaisseClient[]> {
    const loadPages = (): Observable<CaisseClient[]> => {
      try {
        if (!this.lcAuth.getBackStationId() || !this.lcAuth.getLogicielCaisseToken()) {
          return of([] as CaisseClient[]);
        }
      } catch {
        return of([] as CaisseClient[]);
      }

      const pageSize = 100;
      return this.lcClientService.getClients({ page: 1, limit: pageSize }).pipe(
        expand((response) => {
          const currentPage = Number(response.pagination?.page) || 1;
          const totalPages = Number(response.pagination?.totalPages) || 1;
          if (currentPage >= totalPages) {
            return EMPTY;
          }
          return this.lcClientService.getClients({
            page: currentPage + 1,
            limit: pageSize
          });
        }),
        map((response) => response.items ?? []),
        reduce((all, pageItems) => all.concat(pageItems), [] as CaisseClient[]),
        catchError(() => of([] as CaisseClient[]))
      );
    };

    if (this.lcAuth.isReadyForNavigation()) {
      return loadPages();
    }

    return this.lcAuth.bootstrap().pipe(
      switchMap(() => loadPages()),
      catchError(() => of([] as CaisseClient[]))
    );
  }

  /** Affichage liste clients : uniquement logiciel caisse (plus de fusion ERP). */
  private fetchMergedActiveClients(stationId: number): Observable<ErpClient[]> {
    const cached = this.mergedClientsCache.get(stationId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.stream;
    }

    const stream = this.fetchAllCaisseClientsSafe().pipe(
      map((caisse) => (caisse || []).map((row) => this.mapCaisseClientToErp(row))),
      shareReplay(1)
    );
    this.mergedClientsCache.set(stationId, {
      expiresAt: Date.now() + this.mergedClientsCacheTtlMs,
      stream
    });
    return stream;
  }

  private mapCaisseClientToErp(c: CaisseClient): ErpClient {
    const nomPrenom = [c.prenom, c.nom].filter(Boolean).join(' ').trim() || `Client caisse #${c.id}`;
    const normalizedType = this.normalizeClientType(c.type || 'Individuel');
    return {
      // Évite une collision d'identifiants entre les deux bases.
      id: -Math.abs(Number(c.id)),
      type: normalizedType || 'Individuel',
      civilite: String(c.civilite || ''),
      nom_prenom: nomPrenom,
      nom_entreprise: c.nom_entreprise ?? null,
      code_client: c.code_client ?? null,
      raison_social: c.raison_social ?? null,
      forme_juridique: c.forme_juridique ?? null,
      station_id: Number(c.station_id) || 0,
      user_id: Number(c.user_id) || 0,
      identifiant_unique: c.identifiant_unique || `LC-${c.id}`,
      emails: c.email ? [{ email: String(c.email) }] : [],
      telephones: c.telephone ? [{ num_tel: String(c.telephone), code_pays: '' }] : [],
      is_archive: false,
      source: 'caisse',
      created_at: c.created_at ?? undefined,
      updated_at: c.updated_at ?? undefined
    };
  }

  private normalizeClientIdentityPart(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  /** Normalise un numéro (chiffres uniquement, avec code pays si présent). */
  private normalizePhoneValue(numTel?: string | null, codePays?: string | null): string {
    const number = String(numTel || '').replace(/\D+/g, '');
    const country = String(codePays || '').replace(/\D+/g, '');
    if (!number) {
      return '';
    }
    return country && !number.startsWith(country) ? `${country}${number}` : number;
  }

  private collectClientEmails(client: ErpClient): string[] {
    return (client.emails || [])
      .map((item) => String(item?.email || '').trim().toLowerCase())
      .filter((email) => !!email);
  }

  private collectClientPhones(client: ErpClient): string[] {
    return (client.telephones || [])
      .map((item) => this.normalizePhoneValue(item?.num_tel, item?.code_pays))
      .filter((phone) => !!phone);
  }

  private phonesMatch(a: string, b: string): boolean {
    if (!a || !b) {
      return false;
    }
    return a === b || a.endsWith(b) || b.endsWith(a);
  }

  /**
   * Doublon si même type + (même email OU même téléphone OU même identifiant OU même code_client).
   * Évite la double ligne après POST ERP + POST caisse.
   */
  private isSameClientByTypeEmailPhone(displayed: ErpClient, candidate: ErpClient): boolean {
    const displayedType = this.normalizeClientType(displayed.type);
    const candidateType = this.normalizeClientType(candidate.type);
    if (!displayedType || !candidateType || displayedType !== candidateType) {
      return false;
    }

    const displayedRef = this.normalizeClientIdentityPart(displayed.identifiant_unique);
    const candidateRef = this.normalizeClientIdentityPart(candidate.identifiant_unique);
    if (displayedRef && candidateRef && displayedRef === candidateRef) {
      return true;
    }

    const displayedCode = this.normalizeClientIdentityPart(displayed.code_client);
    const candidateCode = this.normalizeClientIdentityPart(candidate.code_client);
    if (displayedCode && candidateCode && displayedCode === candidateCode) {
      return true;
    }

    const displayedEmails = this.collectClientEmails(displayed);
    const candidateEmails = this.collectClientEmails(candidate);
    const emailMatch =
      displayedEmails.length > 0 &&
      candidateEmails.length > 0 &&
      candidateEmails.some((email) => displayedEmails.includes(email));

    const displayedPhones = this.collectClientPhones(displayed);
    const candidatePhones = this.collectClientPhones(candidate);
    const phoneMatch =
      displayedPhones.length > 0 &&
      candidatePhones.length > 0 &&
      candidatePhones.some((phone) =>
        displayedPhones.some((displayedPhone) => this.phonesMatch(displayedPhone, phone))
      );

    // Un seul contact qui matche suffit (plus de AND email∧téléphone trop strict)
    if (emailMatch || phoneMatch) {
      return true;
    }

    // Dernier recours : même nom + prénom normalisé si aucun contact des deux côtés
    const noContacts =
      displayedEmails.length === 0 &&
      candidateEmails.length === 0 &&
      displayedPhones.length === 0 &&
      candidatePhones.length === 0;
    if (noContacts) {
      const displayedName = this.normalizeClientIdentityPart(displayed.nom_prenom);
      const candidateName = this.normalizeClientIdentityPart(candidate.nom_prenom);
      if (displayedName && candidateName && displayedName === candidateName) {
        return true;
      }
    }

    return false;
  }

  /**
   * N'ajoute un client caisse que s'il n'existe pas déjà parmi les clients affichés.
   */
  private filterNewCaisseClients(erpClients: ErpClient[], caisse: CaisseClient[]): ErpClient[] {
    const displayed: ErpClient[] = [...(erpClients || [])];
    const kept: ErpClient[] = [];

    for (const raw of caisse || []) {
      const mapped = this.mapCaisseClientToErp(raw);
      const alreadyShown = displayed.some((client) =>
        this.isSameClientByTypeEmailPhone(client, mapped)
      );
      if (alreadyShown) {
        continue;
      }
      kept.push(mapped);
      displayed.push(mapped);
    }

    return kept;
  }

  /** Clients ERP absents de la caisse (orphelins non synchronisés). */
  private filterErpClientsNotInCaisse(
    erpClients: ErpClient[],
    caisseClients: ErpClient[]
  ): ErpClient[] {
    return (erpClients || []).filter(
      (erp) =>
        !(caisseClients || []).some((caisse) => this.isSameClientByTypeEmailPhone(caisse, erp))
    );
  }

  private fetchAllArchivedClients(stationId: number): Observable<Client[]> {
    const pageSize = 100;

    return this.clientService.getArchivedClient(stationId, 1, pageSize).pipe(
      expand((response) => {
        const currentPage = response.meta?.page ?? 1;
        const totalPages = response.meta?.nb_total_pages ?? response.meta?.pages ?? 1;

        if (currentPage >= totalPages) {
          return EMPTY;
        }

        return this.clientService.getArchivedClient(stationId, currentPage + 1, pageSize);
      }),
      map((response) => response.data ?? []),
      reduce((all, pageData) => all.concat(pageData), [] as Client[])
    );
  }

  private normalizeClientType(type?: string | null): string {
    const value = (type ?? '').trim();
    if (!value) {
      return '';
    }
    const key = value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    if (key === 'individuel') return 'Individuel';
    if (key === 'professionnel') return 'Professionnel';
    if (key === 'negoce') return 'Négoce';
    return value;
  }

  private optionalClientString(value: unknown): string | undefined {
    const normalized = String(value ?? '').trim();
    return normalized || undefined;
  }

  private mapErpClientToCaissePayload(client: Record<string, unknown>): CreateCaisseClientPayload {
    const rawEmails = Array.isArray(client['emails']) ? client['emails'] : [];
    const firstEmail = rawEmails[0];
    const email =
      typeof firstEmail === 'string'
        ? this.optionalClientString(firstEmail)
        : firstEmail && typeof firstEmail === 'object'
          ? this.optionalClientString((firstEmail as Record<string, unknown>)['email'])
          : undefined;

    const rawTelephones = Array.isArray(client['telephones']) ? client['telephones'] : [];
    const firstTelephone =
      rawTelephones[0] && typeof rawTelephones[0] === 'object'
        ? (rawTelephones[0] as Record<string, unknown>)
        : null;
    const phoneNumber = this.optionalClientString(firstTelephone?.['num_tel']);
    const countryCode = this.optionalClientString(firstTelephone?.['code_pays']);
    const telephone =
      phoneNumber && countryCode && !phoneNumber.startsWith(countryCode)
        ? `${countryCode}${phoneNumber}`
        : phoneNumber;

    const normalizedType = this.normalizeClientType(
      this.optionalClientString(client['type'])
    ) as CreateCaisseClientPayload['type'];
    const rawCivilite = this.optionalClientString(client['civilite'])?.toLowerCase();
    const civilite: CreateCaisseClientPayload['civilite'] =
      rawCivilite === 'femme' ? 'Femme' : rawCivilite === 'homme' ? 'Homme' : undefined;

    return {
      // Le modèle ERP ne sépare pas nom/prénom : conserver la valeur complète dans nom.
      nom:
        this.optionalClientString(client['nom_prenom']) ||
        this.optionalClientString(client['nom_entreprise']) ||
        'Client ERP',
      email,
      telephone,
      type: normalizedType || 'Individuel',
      civilite,
      identifiant_unique: this.optionalClientString(client['identifiant_unique']),
      nom_entreprise: this.optionalClientString(client['nom_entreprise']),
      numero_identification_fiscale: this.optionalClientString(
        client['numero_identification_fiscale']
      ),
      code_client: this.optionalClientString(client['code_client']),
      raison_social: this.optionalClientString(client['raison_social']),
      forme_juridique: this.optionalClientString(client['forme_juridique'])
    };
  }

  private syncCreatedClientToCaisse(client: Record<string, unknown>): Observable<void> {
    const sendToCaisse = () =>
      this.lcClientService.createClient(this.mapErpClientToCaissePayload(client)).pipe(
        map(() => undefined)
      );

    if (this.lcAuth.isReadyForNavigation()) {
      return sendToCaisse();
    }

    return this.lcAuth.bootstrap().pipe(switchMap(() => sendToCaisse()));
  }

  addClient(client: Record<string, unknown>): Observable<ErpClient> {
    return (
      this.clientService.addClient(client as unknown as Client) as Observable<ErpClient>
    ).pipe(
      switchMap((erpResponse) => {
        this.invalidateMergedClientsCache(client['station_id']);
        // Fusionner la réponse ERP (id / identifiant) pour un meilleur alignement caisse
        const erpBody =
          erpResponse && typeof erpResponse === 'object'
            ? ((erpResponse as { client?: Record<string, unknown> }).client ??
              (erpResponse as unknown as Record<string, unknown>))
            : {};
        const forCaisse: Record<string, unknown> = {
          ...client,
          ...erpBody,
          // Conserver emails / téléphones du formulaire (structure liste)
          emails: client['emails'] ?? erpBody['emails'],
          telephones: client['telephones'] ?? erpBody['telephones']
        };
        return this.syncCreatedClientToCaisse(forCaisse).pipe(
          map(() => erpResponse),
          catchError((error) => {
            const detail = readObservableError(
              error,
              'La synchronisation avec le logiciel caisse a échoué.'
            );
            this.toastWarning(
              `Client créé dans l’ERP, mais non synchronisé avec la caisse : ${detail}`,
              'Synchronisation partielle'
            );
            // La création ERP est déjà validée : ne pas provoquer une seconde création.
            return of(erpResponse);
          })
        );
      })
    );
  }

  getClientById(clientId: number | string): Observable<ErpClient> {
    return this.clientService.getClientById(clientId) as Observable<ErpClient>;
  }

  updateClient(clientId: number, client: Record<string, unknown>): Observable<ErpClient> {
    this.invalidateMergedClientsCache(client['station_id']);
    return this.clientService.updateClient(clientId, client as Partial<Client>) as Observable<ErpClient>;
  }

  archiveClient(clientId: number, isArchive: boolean): Observable<ErpClient> {
    this.invalidateMergedClientsCache();
    return this.clientService.archiveClient(clientId, isArchive);
  }

  onClientArchiveEvent(): Observable<{ client: ErpClient; is_archive: boolean }> {
    return this.clientEventService.onClientArchiveEvent() as Observable<{
      client: ErpClient;
      is_archive: boolean;
    }>;
  }

  emitClientArchiveEvent(client: ErpClient, isArchive: boolean): void {
    this.clientEventService.emitClientArchiveEvent(client as Client, isArchive);
  }

  navigateToClientsList(): void {
    this.router.navigate(['/bibliotheque-clients/individuel']);
  }

  navigateToAddClient(): void {
    this.router.navigate(['/bibliotheque-clients/add-client']);
  }

  navigateToDuplicateClient(clientId: number | string, client?: ErpClient): void {
    this.router.navigate(['/bibliotheque-clients/add-client'], {
      state: {
        client,
        isDuplicateMode: true,
        sourceClientId: clientId
      }
    });
  }

  navigateToClientDetail(clientId: number | string, client?: ErpClient): void {
    this.router.navigate(this.clientDetailRoute(clientId, 'detail-contenue-apercue'), {
      state: { client }
    });
  }

  navigateToEditClient(clientId: number, client?: ErpClient): void {
    this.router.navigate([`/bibliotheque-clients/edit/${clientId}`], {
      state: { client, isEditMode: true }
    });
  }

  navigateToBulkEditClients(clientIds: number[]): void {
    const ids = [...new Set(clientIds.map((id) => Math.floor(Number(id))).filter((id) => id > 0))];
    if (ids.length < 2) {
      this.toastWarning('Sélectionnez au moins 2 clients pour la modification en série.', 'Information');
      return;
    }
    this.router.navigate(['/bibliotheque-clients/edit-batch'], {
      state: { isBulkEditMode: true, clientIds: ids }
    });
  }

  openClientEmailModal(client: ErpClient): void {
    if (!client.emails?.length || !client.emails[0].email) {
      this.toastError('Aucune adresse e-mail disponible pour ce client.', 'Erreur');
      return;
    }
    window.location.href = `mailto:${client.emails[0].email}`;
  }

  exportClientsTable(format: 'pdf' | 'csv' | 'xlsx'): void {
    const { stationId } = this.getStationContext();
    if (!stationId) {
      this.toastError('Aucune station sélectionnée', 'Erreur');
      return;
    }
    this.clientService.getActiveClient(stationId, 1, 1000).subscribe({
      next: (response: PaginatedResponse<Client>) => {
        const tableData = response.data || [];
        if (!tableData.length) {
          this.toastError('Aucune donnée à exporter', 'Erreur');
          return;
        }
        if (format === 'pdf') {
          this.exportClientsToPdf(tableData);
        } else if (format === 'csv') {
          this.exportClientsToCsv(tableData);
        } else if (format === 'xlsx') {
          this.exportClientsToXlsx(tableData);
        }
      },
      error: (error) => {
        this.toastError(
          error.error?.errors?.map((err: { message: string }) => err.message).join(', ') ||
            'Erreur lors du chargement des clients pour exportation',
          'Erreur'
        );
      }
    });
  }

  downloadClientsImportTemplate(): void {
    void this.loadXlsxModule()
      .then((XLSX) => {
        try {
          const headers = [
            'RÉFÉRENCE',
            'NOM DU CLIENT',
            'EMAIL',
            'TYPE',
            'CIVILITÉ',
            'TÉLÉPHONE',
            'CODE PAYS',
            'TYPE TÉLÉPHONE'
          ];
          const worksheet = XLSX.utils.aoa_to_sheet([headers]);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
          XLSX.writeFile(workbook, 'client_template.xlsx');
          this.toastSuccess('Modèle téléchargé', 'Succès');
        } catch {
          this.toastError('Erreur lors du téléchargement du modèle', 'Erreur');
        }
      })
      .catch(() => this.toastError('Erreur lors du téléchargement du modèle', 'Erreur'));
  }

  importClientsFromXlsxFile(file: File): Observable<{ importedCount: number; errors: string[] }> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        void this.loadXlsxModule()
          .then((XLSX) => {
            try {
              const buffer = e.target?.result;
              if (!buffer) {
                observer.error(new Error('empty file'));
                return;
              }
              const data = new Uint8Array(buffer as ArrayBuffer);
              const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData: unknown[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const expectedHeaders = [
            'RÉFÉRENCE',
            'NOM DU CLIENT',
            'EMAIL',
            'TYPE',
            'CIVILITÉ',
            'TÉLÉPHONE',
            'CODE PAYS',
            'TYPE TÉLÉPHONE'
          ];
          const headers = (jsonData[0] as string[] | undefined)?.map((h) => h?.toString().trim());
          if (!headers || !expectedHeaders.every((h, i) => headers[i] === h)) {
            observer.next({ importedCount: 0, errors: ['Colonnes invalides dans le fichier'] });
            observer.complete();
            return;
          }

          const { userId, stationId } = this.getStationContext();
          const validClientTypes = ['Individuel', 'Professionnel'];
          const validCivilities = ['Homme', 'Femme'];
          const validPhoneTypes = ['mobile', 'fixe', 'fax', 'whatsapp', 'viber', 'support'];
          const rows = jsonData.slice(1);
          if (!rows.length) {
            observer.next({ importedCount: 0, errors: ['Aucune donnée valide trouvée dans le fichier'] });
            observer.complete();
            return;
          }

          let importedCount = 0;
          const errors: string[] = [];
          let processed = 0;

          const finishIfDone = () => {
            processed++;
            if (processed === rows.length) {
              observer.next({ importedCount, errors });
              observer.complete();
            }
          };

          rows.forEach((row, index) => {
            const r = row as unknown[];
            const nom_prenom = r[1]?.toString().trim();
            const type = r[3]?.toString().trim();
            const civilite = r[4]?.toString().trim();
            const line = index + 2;

            if (!nom_prenom) {
              errors.push(`Ligne ${line} : Nom du client manquant.`);
              finishIfDone();
              return;
            }
            if (!type || !validClientTypes.includes(type)) {
              errors.push(`Ligne ${line} : Type de client invalide (${type}).`);
              finishIfDone();
              return;
            }
            if (!civilite || !validCivilities.includes(civilite)) {
              errors.push(`Ligne ${line} : Civilité invalide (${civilite}).`);
              finishIfDone();
              return;
            }

            const client: Client = {
              id: 0,
              identifiant_unique: r[0]?.toString().trim() || null,
              nom_prenom,
              type,
              civilite,
              emails:
                r[2] && r[2].toString().trim()
                  ? [{ email: r[2].toString().trim(), user_id: userId, client_id: 0, station_id: stationId }]
                  : [],
              telephones:
                r[5] && r[6]
                  ? [
                      {
                        id: 0,
                        type:
                          r[7] && validPhoneTypes.includes(r[7].toString().trim())
                            ? r[7].toString().trim()
                            : 'mobile',
                        num_tel: r[5].toString().trim(),
                        code_pays: r[6].toString().trim(),
                        station_id: stationId,
                        user_id: userId,
                        client_id: 0
                      }
                    ]
                  : [],
              station_id: stationId,
              user_id: userId,
              is_archive: false,
              adresses: [],
              informations: []
            } as Client;

            this.clientService.addClient(client).subscribe({
              next: (addedClient) => {
                importedCount++;
                this.clientEventService.emitClientArchiveEvent(addedClient, false);
                finishIfDone();
              },
              error: (error) => {
                const errorMessage =
                  error.error?.errors?.map((err: { message: string }) => err.message).join(', ') ||
                  `Erreur lors de l'ajout du client ${client.nom_prenom}`;
                errors.push(`Ligne ${line} : ${errorMessage}`);
                finishIfDone();
              }
            });
          });
            } catch {
              observer.error(new Error('parse failed'));
            }
          })
          .catch(() => observer.error(new Error('parse failed')));
      };
      reader.onerror = () => observer.error(new Error('read failed'));
      reader.readAsArrayBuffer(file);
    });
  }

  getFournisseursByStation(
    stationId: number,
    page: number,
    limit: number,
    isArchive: boolean
  ): Observable<ErpPaginatedFournisseurs> {
    const req$ = isArchive
      ? this.fournisseurService.getArchivedFournisseur(stationId, page, limit)
      : this.fournisseurService.getActiveFournisseur(stationId, page, limit);
    return req$.pipe(map((r: PaginatedResponse<Fournisseur>) => r as ErpPaginatedFournisseurs));
  }

  getFournisseurTabCounts(stationId: number): Observable<{ active: number; archived: number }> {
    const limitForCount = 1;
    const active$ = this.fournisseurService.getActiveFournisseurCount(stationId);
    const archived$ = this.fournisseurService.getArchivedFournisseurCount(stationId);
    return forkJoin({ active: active$, archived: archived$ });
  }

  archiveFournisseur(fournisseurId: number, isArchive: boolean): Observable<ErpFournisseur> {
    return this.fournisseurService.archiveFournisseur(fournisseurId, isArchive);
  }

  onFournisseurArchiveEvent(): Observable<{ fournisseur: ErpFournisseur; is_archive: boolean }> {
    return this.fournisseurEventService.onFournisseurArchiveEvent() as Observable<{
      fournisseur: ErpFournisseur;
      is_archive: boolean;
    }>;
  }

  emitFournisseurArchiveEvent(fournisseur: ErpFournisseur, isArchive: boolean): void {
    this.fournisseurEventService.emitFournisseurArchiveEvent(fournisseur as Fournisseur, isArchive);
  }

  navigateToFournisseursList(): void {
    this.router.navigate(['/bibliotheque-fournisseurs/actif']);
  }

  navigateToAddFournisseur(): void {
    this.router.navigate(['/bibliotheque-fournisseurs/add-fournisseur']);
  }

  navigateToFournisseurDetail(fournisseurId: number | string, fournisseur?: ErpFournisseur): void {
    this.router.navigate(this.fournisseurDetailRoute(fournisseurId, 'detail-contenue-apercue'), {
      state: { fournisseur }
    });
  }

  navigateToEditFournisseur(fournisseurId: number, fournisseur?: ErpFournisseur): void {
    this.router.navigate([`/bibliotheque-fournisseurs/edit/${fournisseurId}`], {
      state: { fournisseur, isEditMode: true }
    });
  }

  openFournisseurEmailModal(fournisseur: ErpFournisseur): void {
    if (!fournisseur.emails?.length || !fournisseur.emails[0].email) {
      this.toastError('Aucune adresse e-mail disponible pour ce fournisseur.', 'Erreur');
      return;
    }
    window.location.href = `mailto:${fournisseur.emails[0].email}`;
  }

  addFournisseur(fournisseur: Record<string, unknown>): Observable<ErpFournisseur> {
    return this.fournisseurService.addFournisseur(fournisseur as unknown as Fournisseur);
  }

  getFournisseurById(fournisseurId: number | string): Observable<ErpFournisseur> {
    return this.fournisseurService.getFournisseurById(fournisseurId) as Observable<ErpFournisseur>;
  }

  updateFournisseur(fournisseurId: number, fournisseur: Record<string, unknown>): Observable<ErpFournisseur> {
    return this.fournisseurService.updateFournisseur(
      fournisseurId,
      fournisseur as Partial<Fournisseur>
    ) as Observable<ErpFournisseur>;
  }

  exportFournisseursTable(format: 'pdf' | 'csv' | 'xlsx'): void {
    const { stationId } = this.getStationContext();
    if (!stationId) {
      this.toastError('Aucune station sélectionnée', 'Erreur');
      return;
    }
    this.fournisseurService.getActiveFournisseur(stationId, 1, 1000).subscribe({
      next: (response: PaginatedResponse<Fournisseur>) => {
        const tableData = (response.data || []).map((item) => this.mapFournisseurToExportRow(item));
        if (!tableData.length) {
          this.toastError('Aucune donnée à exporter', 'Erreur');
          return;
        }
        if (format === 'pdf') {
          this.exportFournisseursToPdf(tableData);
        } else if (format === 'csv') {
          this.exportFournisseursToCsv(tableData);
        } else if (format === 'xlsx') {
          this.exportFournisseursToXlsx(tableData);
        }
      },
      error: (error) => {
        this.toastError(
          error.error?.errors?.map((err: { message: string }) => err.message).join(', ') ||
            'Erreur lors du chargement des fournisseurs pour exportation',
          'Erreur'
        );
      }
    });
  }

  downloadFournisseursImportTemplate(): void {
    void this.loadXlsxModule()
      .then((XLSX) => {
        try {
          const headers = [
            'RÉFÉRENCE',
            'NOM DU FOURNISSEUR',
            'EMAIL',
            'CIVILITÉ',
            "CHIFFRE D'AFFAIRES",
            'MATRICULE FISCAL',
            'NUMÉROS DE TÉLÉPHONE',
            'ADRESSES'
          ];
          const worksheet = XLSX.utils.aoa_to_sheet([headers]);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
          XLSX.writeFile(workbook, 'fournisseur_template.xlsx');
          this.toastSuccess('Modèle téléchargé', 'Succès');
        } catch {
          this.toastError('Erreur lors du téléchargement du modèle', 'Erreur');
        }
      })
      .catch(() => this.toastError('Erreur lors du téléchargement du modèle', 'Erreur'));
  }

  importFournisseursFromXlsxFile(file: File): Observable<{ importedCount: number; errors: string[] }> {
    return new Observable((observer) => {
      const { userId, stationId } = this.getStationContext();
      if (!stationId || !userId) {
        observer.next({ importedCount: 0, errors: ['Utilisateur ou station non valide'] });
        observer.complete();
        return;
      }

      const validCivilities = ['Homme', 'Femme', 'avant', 'apres'];
      const validAddressTypes = ['facturation', 'livraison'];
      const validPhoneTypes = ['mobile', 'fixe'];
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        void this.loadXlsxModule()
          .then((XLSX) => {
            try {
              const buffer = e.target?.result;
              if (!buffer) {
                observer.error(new Error('empty file'));
                return;
              }
              const data = new Uint8Array(buffer as ArrayBuffer);
              const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData: unknown[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const expectedHeaders = [
            'RÉFÉRENCE',
            'NOM DU FOURNISSEUR',
            'EMAIL',
            'CIVILITÉ',
            "CHIFFRE D'AFFAIRES",
            'MATRICULE FISCAL',
            'NUMÉROS DE TÉLÉPHONE',
            'ADRESSES'
          ];
          const headers = (jsonData[0] as string[] | undefined)?.map((h) => h?.toString().trim());
          if (!headers || !expectedHeaders.every((h, i) => headers[i] === h)) {
            observer.next({ importedCount: 0, errors: ['Colonnes invalides dans le fichier'] });
            observer.complete();
            return;
          }

          const dataRows = jsonData
            .slice(1)
            .filter((row) => (row as unknown[]).some((cell) => cell != null && cell !== ''));
          if (!dataRows.length) {
            observer.next({ importedCount: 0, errors: ['Aucune donnée valide trouvée dans le fichier'] });
            observer.complete();
            return;
          }

          let importedCount = 0;
          const errors: string[] = [];
          let processed = 0;
          const existingIds = new Set<string>();

          const finishIfDone = () => {
            processed++;
            if (processed === dataRows.length) {
              observer.next({ importedCount, errors });
              observer.complete();
            }
          };

          dataRows.forEach((row, index) => {
            const r = row as unknown[];
            const line = index + 2;
            const identifiant_unique = r[0] != null ? r[0].toString().trim() : '';
            const nom_entreprise = r[1] != null ? r[1].toString().trim() : '';
            const email = r[2] != null ? r[2].toString().trim() : '';
            const civilite = r[3] != null ? r[3].toString().trim() : '';
            const numero_identification_fiscale_raw = r[5] != null ? r[5].toString().trim() : '';
            let numero_identification_fiscale = numero_identification_fiscale_raw;

            if (
              numero_identification_fiscale.includes('E') ||
              numero_identification_fiscale.includes('e')
            ) {
              try {
                numero_identification_fiscale = Number(numero_identification_fiscale_raw).toFixed(0);
              } catch {
                errors.push(
                  `Ligne ${line} : Matricule fiscal invalide (${numero_identification_fiscale_raw}).`
                );
                finishIfDone();
                return;
              }
            }

            if (!identifiant_unique) {
              errors.push(`Ligne ${line} : Référence manquante.`);
              finishIfDone();
              return;
            }
            if (existingIds.has(identifiant_unique)) {
              errors.push(`Ligne ${line} : Référence dupliquée (${identifiant_unique}).`);
              finishIfDone();
              return;
            }
            existingIds.add(identifiant_unique);
            if (!nom_entreprise) {
              errors.push(`Ligne ${line} : Nom du fournisseur manquant.`);
              finishIfDone();
              return;
            }
            if (!email || !/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
              errors.push(`Ligne ${line} : Email invalide (${email || 'vide'}).`);
              finishIfDone();
              return;
            }
            if (!civilite || !validCivilities.includes(civilite)) {
              errors.push(
                `Ligne ${line} : Civilité invalide (${civilite || 'vide'}). Doit être "Homme", "Femme", "avant" ou "apres".`
              );
              finishIfDone();
              return;
            }
            if (numero_identification_fiscale && !/^\d{9}$/.test(numero_identification_fiscale)) {
              errors.push(
                `Ligne ${line} : Matricule fiscal invalide (${numero_identification_fiscale || 'vide'}). Doit contenir 9 chiffres.`
              );
              finishIfDone();
              return;
            }

            const phoneNumbers = r[6]
              ? r[6]
                  .toString()
                  .split(';')
                  .map((num: string) => num.trim())
                  .filter(
                    (num: string) =>
                      num && /^\+?\d{8,}$/.test(num.replace(/^\+\d{1,3}/, ''))
                  )
              : [];
            const addresses = r[7]
              ? r[7]
                  .toString()
                  .split('|')
                  .map((addr: string) => {
                    const [addressType, pays, region, codePostal, adresse] = addr
                      .split(',')
                      .map((s: string) => s?.trim() || '');
                    return {
                      type:
                        addressType && validAddressTypes.includes(addressType)
                          ? addressType
                          : 'facturation',
                      pays: pays || '',
                      region: region || '',
                      code_postal: codePostal || '',
                      adresse: adresse || '',
                      station_id: stationId,
                      user_id: userId,
                      fournisseur_id: 0
                    };
                  })
                  .filter(
                    (addr) =>
                      addr.type && addr.pays && addr.region && addr.code_postal && addr.adresse
                  )
              : [];

            const fournisseur = {
              id: 0,
              identifiant_unique,
              nom_entreprise,
              emails: email,
              civilite,
              numero_identification_fiscale: numero_identification_fiscale || null,
              site_web: null,
              station_id: stationId,
              user_id: userId,
              is_archive: false,
              telephones: phoneNumbers.map((num: string) => ({
                id: 0,
                num_tel: num.replace(/^\+\d{1,3}/, '') || '',
                code_pays: num.match(/^\+\d{1,3}/)?.[0] || '+216',
                type:
                  num.includes('+') && validPhoneTypes.includes('mobile') ? 'mobile' : 'fixe',
                station_id: stationId,
                user_id: userId,
                fournisseur_id: 0
              })),
              adresses: addresses
            } as unknown as Fournisseur;

            this.fournisseurService.addFournisseur(fournisseur).subscribe({
              next: (addedFournisseur) => {
                importedCount++;
                this.fournisseurEventService.emitFournisseurArchiveEvent(addedFournisseur, false);
                finishIfDone();
              },
              error: (error) => {
                const errorMessage =
                  error.error?.errors?.map((err: { message: string }) => err.message).join(', ') ||
                  `Erreur lors de l'ajout du fournisseur ${fournisseur.nom_entreprise}`;
                errors.push(`Ligne ${line} : ${errorMessage}`);
                finishIfDone();
              }
            });
          });
            } catch {
              observer.error(new Error('parse failed'));
            }
          })
          .catch(() => observer.error(new Error('parse failed')));
      };
      reader.onerror = () => observer.error(new Error('read failed'));
      reader.readAsArrayBuffer(file);
    });
  }

  setPageHeader(config: ErpPageHeaderConfig): void {
    const headerConfig: HeaderConfig = {
      title: config.title,
      buttons: (config.buttons ?? []) as HeaderConfig['buttons']
    };
    this.headerService.setConfig(headerConfig);
  }

  getTvaMessageEmitter() {
    return this.tvaService.messageEmitter;
  }

  onParamModalOpen() {
    return this.parametreService.openModal$;
  }

  ensureParamRefModules(): Observable<unknown[]> {
    return this.paramDonneesState.ensureRefModules();
  }

  getParamModuleRef(moduleKey: string): { table_db_id?: number; id?: number } | undefined {
    return this.paramDonneesState.getModuleRef(moduleKey);
  }

  getParamTablesByTable(tableDbOrId: number | string, stationId: number, userId: number): Observable<unknown> {
    return this.paramTablesService.getParamTablesByTable(tableDbOrId, stationId, userId);
  }

  getSelectParamsByParamTable(paramTableId: number, stationId: number, userId: number): Observable<unknown> {
    return this.paramTablesService.getSelectParamsByParamTable(paramTableId, stationId, userId);
  }

  getCoordinateParamsByParamTableProduct(
    paramTableId: number,
    productId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    return this.paramTablesService.getCoordinateParamsByParamTableProduct(
      paramTableId,
      productId,
      stationId,
      userId
    );
  }

  getCoordinateParamsByParamTableClient(
    paramTableId: number,
    clientId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    return this.paramTablesService.getCoordinateParamsByParamTableClient(
      paramTableId,
      clientId,
      stationId,
      userId
    );
  }

  getCoordinateParamsByParamTableFournisseur(
    paramTableId: number,
    fournisseurId: number,
    stationId: number,
    userId: number
  ): Observable<unknown> {
    return this.paramTablesService.getCoordinateParamsByParamTableFournisseur(
      paramTableId,
      fournisseurId,
      stationId,
      userId
    );
  }

  postCoordinateParams(payloads: Record<string, unknown>[]): Observable<unknown> {
    return this.paramTablesService.postCoordinateParams(payloads as never);
  }

  openTvaModal(type: 'ajouter' | 'update' | 'etat', item?: unknown, statut?: boolean): void {
    if (this.tvaModalRef) {
      return;
    }
    this.tvaModalRef = this.modalService.open(ErpCroudTvaComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    const modalRef = this.tvaModalRef;
    modalRef.result.finally(() => {
      if (this.tvaModalRef === modalRef) {
        this.tvaModalRef = null;
      }
    });
    setTimeout(() => {
      const instance = modalRef.componentInstance as ErpCroudTvaComponent;
      instance.openModal(type, item as never, statut);
      instance.tvaAdded.subscribe(() => this.tvaAdded$.next());
      instance.tvaUpdated.subscribe((tva) => this.tvaUpdated$.next(tva));
    }, 0);
  }

  openTaxeModal(type: 'ajouter' | 'update' | 'etat', item?: unknown, statut?: boolean): void {
    if (this.taxeModalRef) {
      return;
    }
    this.taxeModalRef = this.modalService.open(ErpCroudTaxeComponent, {
      size: 'lg',
      windowClass: 'custom-modal-width',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    const modalRef = this.taxeModalRef;
    modalRef.result.finally(() => {
      if (this.taxeModalRef === modalRef) {
        this.taxeModalRef = null;
      }
    });
    setTimeout(() => {
      const instance = modalRef.componentInstance as ErpCroudTaxeComponent;
      instance.openModal(type, item as never, statut);
      instance.taxeAdded.subscribe(() => this.taxeAdded$.next());
      instance.taxeUpdated.subscribe((taxe) => this.taxeUpdated$.next(taxe));
    }, 0);
  }

  openMarqueModal(stationId: number): Observable<{ id: number; libelle: string } | null> {
    return this.openMarqueCategoryModal('marque', stationId);
  }

  openCategoryModal(stationId: number): Observable<{ id: number; libelle: string } | null> {
    return this.openMarqueCategoryModal('catégorie', stationId);
  }

  openUniteModal(stationId: number): Observable<{ id: number; libelle: string } | null> {
    return this.openMarqueCategoryModal('unité', stationId);
  }

  onTvaAdded(): Observable<void> {
    return this.tvaAdded$.asObservable();
  }

  onTvaUpdated(): Observable<ErpTva> {
    return this.tvaUpdated$.asObservable();
  }

  onTaxeAdded(): Observable<void> {
    return this.taxeAdded$.asObservable();
  }

  onTaxeUpdated(): Observable<ErpTaxe> {
    return this.taxeUpdated$.asObservable();
  }

  toastSuccess(message: string, title?: string, options?: Record<string, unknown>): void {
    this.toastr.success(message, title || '', {
      positionClass: 'toast-bottom-right',
      toastClass: 'toast ngx-toastr custom-success-toast',
      ...options
    });
  }

  toastError(message: string, title?: string, options?: Record<string, unknown>): void {
    this.toastr.error(message, title || '', {
      positionClass: 'toast-bottom-right',
      toastClass: 'toast ngx-toastr custom-error-toast',
      ...options
    });
  }

  toastInfo(message: string, title?: string, options?: Record<string, unknown>): void {
    this.toastr.info(message, title || '', {
      positionClass: 'toast-bottom-right',
      toastClass: 'toast ngx-toastr',
      ...(options as object)
    });
  }

  toastWarning(message: string, title?: string): void {
    this.toastr.warning(message, title || '', {
      positionClass: 'toast-bottom-right',
      toastClass: 'toast ngx-toastr'
    });
  }

  replaceFeatherIcons(): void {
    try {
      feather.replace();
    } catch {
      /* ignore */
    }
  }

  bulkAddListe(payload: GammeListeBulkRequest) {
    return this.productsService.bulkAddListe(payload);
  }

  navigateBack(): void {
    this.location.back();
  }

  createAvanceEspeceClient(
    clientId: number,
    stationId: number,
    data: { solde_avance?: number; note_avance?: string; user_id: number }
  ): Observable<unknown> {
    return this.tresorerieService.createAvanceEspeceClient(clientId, stationId, data);
  }

  getStatsClient(clientId: number): Observable<{ success: boolean; data?: Record<string, unknown> }> {
    return this.documentService.getStatsClient(clientId);
  }

  getAvancesEspeceClient(clientId: number, stationId?: number): Observable<unknown[]> {
    return this.tresorerieService.getAvancesEspeceClient(clientId, stationId) as Observable<unknown[]>;
  }

  getTypeDocumentIdByTitre(titre: string): Observable<number | null> {
    return this.documentService.getTypeDocumentIdByTitre(titre);
  }

  getStatutIdsForReleveClient(): Observable<{ statutsAvoirIds: number[]; statutPayeId: number | null }> {
    return this.documentService.getStatutIdsForReleveClient();
  }

  getDocumentsByClientAll(
    clientId: number,
    pageSize?: number,
    session?: { station_id?: number; user_id?: number }
  ): Observable<unknown[]> {
    return this.documentService.getDocumentsByClientAll(clientId, pageSize, session);
  }

  getDocumentsByClientStatutAll(
    clientId: number,
    statutId: number,
    pageSize?: number,
    session?: { station_id?: number; user_id?: number }
  ): Observable<unknown[]> {
    return this.documentService.getDocumentsByClientStatutAll(clientId, statutId, pageSize, session);
  }

  getDocumentsByClientStatut(
    clientId: number,
    statutId: number,
    page?: number,
    limit?: number,
    session?: { station_id?: number; user_id?: number }
  ): Observable<unknown> {
    return this.documentService.getDocumentsByClientStatut(clientId, statutId, page, limit, session);
  }

  getPaiementsByDocumentId(documentId: number): Observable<unknown> {
    return this.paiementService.getPaiementsByDocumentId(documentId);
  }

  getDocumentSharedComponent(): Type<unknown> | null {
    return SharedComponent;
  }

  clientDetailRoute(reference: string | number, ...childSegments: string[]): (string | number)[] {
    return ['/bibliotheque-clients/details', reference, ...childSegments];
  }

  getStatsFournisseur(fournisseurId: number): Observable<{ success: boolean; data?: Record<string, unknown> }> {
    return this.documentService.getStatsFournisseur(fournisseurId);
  }

  onFournisseurDetailUpdate(): Observable<ErpFournisseur | null> {
    return this.fournisseurEventService.getFournisseur() as Observable<ErpFournisseur | null>;
  }

  setFournisseurDetailState(fournisseur: ErpFournisseur | null): void {
    this.fournisseurEventService.updateFournisseur(fournisseur as Fournisseur | null);
  }

  fournisseurDetailRoute(reference: string | number, ...childSegments: string[]): (string | number)[] {
    return ['/bibliotheque-fournisseurs/details', reference, ...childSegments];
  }

  resolveFournisseurDocumentsApiId(reference: string | number): Observable<number | null> {
    return this.getFournisseurById(reference).pipe(
      map((f) => {
        const business = f.fournisseur_id;
        if (business != null && !Number.isNaN(Number(business))) {
          return Number(business);
        }
        if (f.id != null && !Number.isNaN(Number(f.id))) {
          return Number(f.id);
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  getDocumentsByFournisseurStatutAll(
    fournisseurId: number,
    statutId: number,
    pageSize?: number
  ): Observable<unknown[]> {
    return this.documentService.getDocumentsByFournisseurStatutAll(fournisseurId, statutId, pageSize);
  }

  navigateToCreateStation(): void {
    this.router.navigate(['/create-station']);
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  private getProduitsRefModuleSilent(): { tableDbId: number } | null {
    const ref =
      this.paramDonneesState.getModuleRef('products') ?? this.paramDonneesState.getModuleRef('produits');
    if (!ref) {
      return null;
    }
    const tableDbId = resolveRefModuleTableDbId(ref);
    if (!Number.isFinite(tableDbId) || tableDbId <= 0) {
      return null;
    }
    return { tableDbId };
  }

  private getProduitsRefModuleOrWarn(): { tableDbId: number } | null {
    const ref =
      this.paramDonneesState.getModuleRef('products') ?? this.paramDonneesState.getModuleRef('produits');
    if (!ref) {
      this.toastError(
        'Module « products » introuvable dans ref_data_module (vérifiez GET /api/parametrage/data-modules).',
        'Export'
      );
      return null;
    }
    const tableDbId = resolveRefModuleTableDbId(ref);
    if (!Number.isFinite(tableDbId) || tableDbId <= 0) {
      this.toastError('Identifiant table (table_db_id / id) invalide pour le module « products ».', 'Export');
      return null;
    }
    return { tableDbId };
  }

  private iconForProductExportType(type: string): string | undefined {
    const t = String(type || '').toLowerCase().trim();
    if (t === 'pdf') return '../../../assets/images/logo/pdf_logo.png';
    if (t === 'csv') return '../../../assets/images/logo/csv_logo.png';
    if (t === 'xlsx' || t === 'xls') return '../../../assets/images/logo/xlsx_logo.png';
    return 'assets/images/logo/Fichier 1.png';
  }

  private exportProductsListUsingModel(_exportFormatId: number, type: string): void {
    const { stationId } = this.getStationContext();
    if (!stationId) {
      this.toastError('Aucune station sélectionnée', 'Erreur');
      return;
    }

    this.productsService.getProductsByStationId(stationId, 1, 1000, false).subscribe({
      next: (response) => {
        const data = response.data || [];
        if (!data.length) {
          this.toastWarning('Aucune donnée disponible pour l\'exportation', 'Avertissement');
          return;
        }
        const exportType = String(type || '').toLowerCase().trim();
        if (exportType === 'pdf') {
          this.exportProductsListToPdf(data);
        } else if (exportType === 'csv') {
          this.exportProductsListToCsv(data);
        } else if (exportType === 'xlsx' || exportType === 'xls') {
          this.exportProductsListToXlsx(data);
        } else {
          this.toastWarning(`Type d’export non supporté : ${exportType || 'inconnu'}`, 'Export');
        }
      },
      error: () => this.toastError('Erreur lors du chargement des produits pour exportation', 'Erreur')
    });
  }

  private exportProductsListToPdf(data: Product[]): void {
    void this.loadJsPdfBundle()
      .then(({ jsPDF, autoTable }) => {
        try {
          const doc = new jsPDF() as {
            setFontSize: (size: number) => void;
            text: (text: string, x: number, y: number) => void;
            save: (filename: string) => void;
          };
          doc.setFontSize(16);
          doc.text('LISTE DES PRODUITS', 14, 15);
          doc.setFontSize(10);
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, 14, 22);
          doc.text(`Total des produits: ${data.length}`, 14, 29);
          autoTable(doc, {
            head: [['RÉFÉRENCE', 'NOM', 'PRIX DE VENTE']],
            body: data.map((item) => [
              item.ref_interne || item.ref_fabricant || '---',
              item.nom || '---',
              item.prix_unitaires != null ? String(item.prix_unitaires) : item.result_ttc != null ? String(item.result_ttc) : '---'
            ]),
            startY: 35,
            theme: 'grid',
            headStyles: { fillColor: [19, 74, 211] },
            styles: { fontSize: 9 }
          });
          doc.save('produits.pdf');
          this.toastSuccess('Exportation PDF réussie', 'Succès');
        } catch {
          this.toastError("Erreur lors de l'exportation PDF", 'Erreur');
        }
      })
      .catch(() => this.toastError("Erreur lors de l'exportation PDF", 'Erreur'));
  }

  private exportProductsListToCsv(data: Product[]): void {
    try {
      const headers = ['RÉFÉRENCE,NOM,PRIX DE VENTE'];
      const rows = data.map(
        (item) =>
          `"${item.ref_interne || item.ref_fabricant || ''}","${item.nom || ''}","${item.prix_unitaires ?? item.result_ttc ?? ''}"`
      );
      const csvContent = headers.concat(rows).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'produits.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.toastSuccess('Exportation CSV réussie', 'Succès');
    } catch {
      this.toastError("Erreur lors de l'exportation CSV", 'Erreur');
    }
  }

  private exportProductsListToXlsx(data: Product[]): void {
    void this.loadXlsxModule()
      .then((XLSX) => {
        try {
          const worksheetData = data.map((item) => ({
            RÉFÉRENCE: item.ref_interne || item.ref_fabricant || '',
            NOM: item.nom || '',
            'PRIX DE VENTE': item.prix_unitaires ?? item.result_ttc ?? ''
          }));
          const worksheet = XLSX.utils.json_to_sheet(worksheetData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Produits');
          XLSX.writeFile(workbook, 'produits.xlsx');
          this.toastSuccess('Exportation XLSX réussie', 'Succès');
        } catch {
          this.toastError("Erreur lors de l'exportation XLSX", 'Erreur');
        }
      })
      .catch(() => this.toastError("Erreur lors de l'exportation XLSX", 'Erreur'));
  }

  private mapFournisseurToExportRow(fournisseur: Fournisseur): {
    reference: string;
    fournisseursName: string;
    email: string;
    gender: string;
    chiffre_aff: string;
    matricule: string;
    phoneNumbers: string[];
    addresses: { addressType: string; pays: string; region: string; codePostal: string; adresse: string }[];
  } {
    return {
      reference: fournisseur.identifiant_unique || '',
      fournisseursName: fournisseur.nom_entreprise || '',
      email: fournisseur.emails?.[0]?.email || '',
      gender: fournisseur.civilite || '',
      chiffre_aff: '',
      matricule: fournisseur.numero_identification_fiscale || '',
      phoneNumbers: fournisseur.telephones?.map((tel) => `${tel.code_pays}${tel.num_tel}`) || [],
      addresses:
        fournisseur.adresses?.map((addr) => ({
          addressType: addr.type || '',
          pays: addr.pays || '',
          region: addr.region || '',
          codePostal: addr.code_postal || '',
          adresse: addr.adresse || ''
        })) || []
    };
  }

  private exportFournisseursToPdf(
    data: ReturnType<ErpAffichagesApiService['mapFournisseurToExportRow']>[]
  ): void {
    void this.loadJsPdfBundle()
      .then(({ jsPDF, autoTable }) => {
        try {
          const doc = new jsPDF() as { save: (filename: string) => void };
          autoTable(doc, {
            columns: [
              { header: 'RÉFÉRENCE', dataKey: 'reference' },
              { header: 'NOM DU FOURNISSEUR', dataKey: 'fournisseursName' },
              { header: "CHIFFRE D'AFFAIRES", dataKey: 'chiffre_aff' },
              { header: 'MATRICULE FISCAL', dataKey: 'matricule' }
            ],
            body: data.map((item) => ({
              reference: item.reference || '',
              fournisseursName: `${item.fournisseursName || ''} (${item.email || ''})`,
              chiffre_aff: item.chiffre_aff || '',
              matricule: item.matricule || ''
            })),
            theme: 'striped',
            headStyles: { fillColor: [115, 103, 240] },
            styles: { fontSize: 10 }
          });
          doc.save('fournisseurs.pdf');
          this.toastSuccess('Exportation PDF réussie', 'Succès');
        } catch {
          this.toastError("Erreur lors de l'exportation PDF", 'Erreur');
        }
      })
      .catch(() => this.toastError("Erreur lors de l'exportation PDF", 'Erreur'));
  }

  private exportFournisseursToCsv(
    data: ReturnType<ErpAffichagesApiService['mapFournisseurToExportRow']>[]
  ): void {
    try {
      const headers = [
        "RÉFÉRENCE,NOM DU FOURNISSEUR,EMAIL,CIVILITÉ,CHIFFRE D'AFFAIRES,MATRICULE FISCAL,NUMÉROS DE TÉLÉPHONE,ADRESSES"
      ];
      const rows = data.map(
        (item) =>
          `"${item.reference || ''}","${item.fournisseursName || ''}","${item.email || ''}","${item.gender || ''}","${item.chiffre_aff || ''}","${item.matricule || ''}","${(item.phoneNumbers || []).join(';')}","${(item.addresses || []).map((addr) => `${addr.addressType},${addr.pays},${addr.region},${addr.codePostal},${addr.adresse}`).join('|')}"`
      );
      const csvContent = headers.concat(rows).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'fournisseurs.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.toastSuccess('Exportation CSV réussie', 'Succès');
    } catch {
      this.toastError("Erreur lors de l'exportation CSV", 'Erreur');
    }
  }

  private exportFournisseursToXlsx(
    data: ReturnType<ErpAffichagesApiService['mapFournisseurToExportRow']>[]
  ): void {
    void this.loadXlsxModule()
      .then((XLSX) => {
        try {
          const worksheetData = data.map((item) => ({
            RÉFÉRENCE: item.reference || '',
            'NOM DU FOURNISSEUR': item.fournisseursName || '',
            EMAIL: item.email || '',
            CIVILITÉ: item.gender || '',
            "CHIFFRE D'AFFAIRES": item.chiffre_aff || '',
            'MATRICULE FISCAL': item.matricule || '',
            'NUMÉROS DE TÉLÉPHONE': (item.phoneNumbers || []).join(';'),
            ADRESSES: (item.addresses || [])
              .map((addr) => `${addr.addressType},${addr.pays},${addr.region},${addr.codePostal},${addr.adresse}`)
              .join('|')
          }));
          const worksheet = XLSX.utils.json_to_sheet(worksheetData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Fournisseurs');
          XLSX.writeFile(workbook, 'fournisseurs.xlsx');
          this.toastSuccess('Exportation XLSX réussie', 'Succès');
        } catch {
          this.toastError("Erreur lors de l'exportation XLSX", 'Erreur');
        }
      })
      .catch(() => this.toastError("Erreur lors de l'exportation XLSX", 'Erreur'));
  }

  private exportClientsToPdf(data: Client[]): void {
    void this.loadJsPdfBundle()
      .then(({ jsPDF, autoTable }) => {
        try {
          const doc = new jsPDF() as { save: (filename: string) => void };
          autoTable(doc, {
            columns: [
              { header: 'RÉFÉRENCE', dataKey: 'identifiant_unique' },
              { header: 'NOM DU CLIENT', dataKey: 'nom_prenom' },
              { header: 'REVENUS', dataKey: 'revenue' },
              { header: 'MATRICULE FISCAL', dataKey: 'matricule' }
            ],
            body: data.map((item) => ({
              identifiant_unique: item.identifiant_unique || item.id?.toString() || 'N/A',
              nom_prenom: `${item.nom_prenom || 'N/A'} (${item.emails?.[0]?.email || 'N/A'})`,
              revenue: 'N/A',
              matricule: item.identifiant_unique || item.id?.toString() || 'N/A'
            })),
            theme: 'striped',
            headStyles: { fillColor: [115, 103, 240] },
            styles: { fontSize: 10 }
          });
          doc.save('clients.pdf');
          this.toastSuccess('Exportation PDF réussie', 'Succès');
        } catch {
          this.toastError("Erreur lors de l'exportation PDF", 'Erreur');
        }
      })
      .catch(() => this.toastError("Erreur lors de l'exportation PDF", 'Erreur'));
  }

  private exportClientsToCsv(data: Client[]): void {
    try {
      const headers = ['RÉFÉRENCE,NOM DU CLIENT,EMAIL,TYPE,CIVILITÉ,TÉLÉPHONE,CODE PAYS'];
      const rows = data.map(
        (item) =>
          `"${item.identifiant_unique || item.id || 'N/A'}","${item.nom_prenom || 'N/A'}","${item.emails?.[0]?.email || 'N/A'}","${item.type || 'N/A'}","${item.civilite || 'N/A'}","${item.telephones?.[0]?.num_tel || 'N/A'}","${item.telephones?.[0]?.code_pays || 'N/A'}"`
      );
      const csvContent = headers.concat(rows).join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'clients.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.toastSuccess('Exportation CSV réussie', 'Succès');
    } catch {
      this.toastError("Erreur lors de l'exportation CSV", 'Erreur');
    }
  }

  private exportClientsToXlsx(data: Client[]): void {
    void this.loadXlsxModule()
      .then((XLSX) => {
        try {
          const exportData = data.map((item) => ({
            RÉFÉRENCE: item.identifiant_unique || item.id || 'N/A',
            'NOM DU CLIENT': item.nom_prenom || 'N/A',
            EMAIL: item.emails?.[0]?.email || 'N/A',
            TYPE: item.type || 'N/A',
            CIVILITÉ: item.civilite || 'N/A',
            TÉLÉPHONE: item.telephones?.[0]?.num_tel || 'N/A',
            'CODE PAYS': item.telephones?.[0]?.code_pays || 'N/A'
          }));
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
          XLSX.writeFile(workbook, 'clients.xlsx');
          this.toastSuccess('Exportation XLSX réussie', 'Succès');
        } catch {
          this.toastError("Erreur lors de l'exportation XLSX", 'Erreur');
        }
      })
      .catch(() => this.toastError("Erreur lors de l'exportation XLSX", 'Erreur'));
  }

  private openMarqueCategoryModal(
    subType: string,
    stationId: number
  ): Observable<{ id: number; libelle: string } | null> {
    const modalRef = this.modalService.open(ErpCroudMarqueCategoryComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.modalSubType = subType;
    modalRef.componentInstance.idStation = stationId;
    return from(modalRef.result).pipe(
      map((result) => result as { id: number; libelle: string } | null),
      catchError(() => of(null))
    );
  }
}
